import { useWebsocketStore } from "../websocket";
import { useDashboardState } from "./dashboard-state";
import { useTimeRangeManager } from "./dashboard-time";

/**
 * Work in the background, by looking at the dashboard state
 * If the dashboard live mode is enabled, will update the range, based on the data sent from the api.
 */
export function provideDashboardLiveData() {
	const { socket } = useWebsocketStore();

	const state = useDashboardState();
	const timeManager = useTimeRangeManager();

	async function listen() {
		if (state.context.value == null || !socket.connected) {
			return;
		}

		const { site } = state.context.value;
		await socket.request("LiveData.Listen", { type: "site", id: site });
	}

	async function unlisten() {
		if (state.context.value == null || !socket.connected) {
			return;
		}

		const { site } = state.context.value;
		await socket.request("LiveData.Unlisten", { type: "site", id: site });
	}

	async function updateSocketDataSubscription() {
		await unlisten();

		if (state.liveMode.value) {
			await listen();
		}
	}

	watch([state.context, state.liveMode], updateSocketDataSubscription);

	socket.on("open", updateSocketDataSubscription);
	onScopeDispose(() => socket.off("open", updateSocketDataSubscription));

	let lastDataStart: Date | null = null;

	type LiveDataUpdate = {
		id: string;
		data: { time: string; period: number };
	};
	function onLiveData({ id, data }: LiveDataUpdate) {
		if (id === state.context.value?.site) {
			const { range, updateRange, zoomRange } = timeManager;

			const dataEnd = new Date(data.time);
			const dataStart = new Date(dataEnd.valueOf() - data.period * 1_000);

			if (lastDataStart != null && lastDataStart > dataStart) {
				// Handle when mezzo send a data block with a longer period than the others.
				return;
			}

			lastDataStart = dataStart;

			const offset = dataEnd.valueOf() - range.value.end.valueOf();
			const newStart = new Date(range.value.start.valueOf() + offset);

			updateRange({ start: newStart, end: dataEnd }, false);
			timeManager.updateZoom({
				start: zoomRange.value.start.plus(offset),
				end: zoomRange.value.end.plus(offset),
			});

			if (state.lockCursor.value) {
				const newCursor = (dataStart.valueOf() + dataEnd.valueOf()) / 2;
				timeManager.updateCursor(new Date(newCursor));
			}
		}
	}

	socket.on("LiveData.Update", onLiveData);
	onScopeDispose(() => socket.off("LiveData.Update", onLiveData));
}
